import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  booleanAttribute,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { filter, map, switchMap } from 'rxjs';

import { DESTROYED_SIGNAL, SvgIconComponent, isDefined, resizeObservable } from '@dmb/core';

import { PREFERS_REDUCED_MOTION } from '../../product/wizard/utils';

@Component({
  selector: 'dmb-expansion-panel',
  templateUrl: './expansion-panel.component.html',
  styleUrls: ['./expansion-panel.component.scss'],
  imports: [SvgIconComponent],
  host: {
    '[attr.collapsible]': 'collapsible()',
    '[attr.expanded]': 'expanded()',
    '(expansion_panel_open)': 'expanded.set(true)',
    '(expansion_panel_close)': 'collapsible() ? expanded.set(false) : undefined',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExpansionPanelComponent {
  private el = inject<ElementRef<HTMLElement>>(ElementRef);
  private contentDiv = viewChild.required<ElementRef<HTMLElement>>('content');
  private prefersReducedMotion = inject(PREFERS_REDUCED_MOTION);

  collapsible = input(true, { transform: booleanAttribute });
  showIcon = input(undefined, { transform: booleanAttribute });
  effectiveShowIcon = computed(() => this.showIcon() ?? this.collapsible());

  expanded = model(true);
  variant = input<'primary' | 'secondary'>('primary');
  placeIconAtEnd = input(false, { transform: booleanAttribute });
  isOpen = output<boolean>();
  effectivePlaceIconAtEnd = computed(() => this.variant() === 'secondary' || this.placeIconAtEnd());

  private destroyed = inject(DESTROYED_SIGNAL);
  animationRunning = signal(false);

  private contentHeight = toSignal(
    toObservable(this.contentDiv).pipe(
      filter(isDefined),
      switchMap((ref) => resizeObservable(ref.nativeElement)),
      map((entries) => entries[0]?.borderBoxSize[0]?.blockSize ?? 0),
      takeUntilDestroyed(),
    ),
    { initialValue: undefined },
  );

  // Expanding waits for the next frame: content created together with the expansion (e.g. rendered lazily)
  // is laid out and measured by then, so the animation runs to its real height.
  private expandFrameReached = signal(this.expanded());
  maxHeight = computed(() => {
    if (!this.expanded()) {
      return '0';
    }

    const contentHeight = this.contentHeight();
    if (contentHeight === undefined) {
      // Initial state when expanded. We don't have a height yet.
      return 'none';
    }

    return this.expandFrameReached() ? `${contentHeight}px` : '0';
  });

  constructor() {
    effect((onCleanup) => {
      if (!this.expanded()) {
        this.expandFrameReached.set(false);
        return;
      }

      const handle = requestAnimationFrame(() => this.expandFrameReached.set(true));
      onCleanup(() => cancelAnimationFrame(handle));
    });

    effect((onCleanup) => {
      this.expanded();

      const animationDuration = untracked(() => this.prefersReducedMotion()) ? 0 : 1000;

      this.animationRunning.set(true);
      const handle = setTimeout(() => {
        this.animationRunning.set(false);
        this.toggleComplete();
      }, animationDuration);

      onCleanup(() => {
        clearTimeout(handle);
      });
    });
  }

  toggleExpansion() {
    if (this.collapsible()) {
      this.expanded.update((e) => !e);
    }
  }

  toggleComplete() {
    if (!this.destroyed()) {
      this.isOpen.emit(this.expanded());
      this.el.nativeElement.dispatchEvent(new CustomEvent('expansion_panel_toggle_complete'));
    }
  }
}
